import Service, { type Registry as Services, service } from '@ember/service';
import type { Hotel } from 'frontend-utils/models';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import type { FormatUrlParams } from 'frontend-utils/utils/url/format-url';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { Parity, ParityStrategyIssue } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import {
  COMPONENT_DOMAINS,
  RATETYPES,
} from 'lumen/utils/constants/general-constants';

const CONSOLE_LAYOUT = 'color: #005EA0; font-weight: bold;';

export default class ParityLoggerService extends Service {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  logFullsUrl(redirectParams: FormatUrlParams): void {
    const path = '/redirect/fulls/';
    const { host } = this.ajax;
    const fullsUrl = formatUrl(path, redirectParams, host);
    console.log(`%cFulls: ${encodeURI(fullsUrl)}`, CONSOLE_LAYOUT);
  }

  logMultiViewUrl(multiViewParams: FormatUrlParams): void {
    const path = `https://viewer.${DOMAIN_NAME}/multiview`;
    const multiViewUrl = formatUrl(path, multiViewParams);
    console.log(`%cMultiview: ${encodeURI(multiViewUrl)}`, CONSOLE_LAYOUT);
  }

  logParityScreenshot(parity: Parity): void {
    if (!this.access.isStaffUser || !console) {
      return;
    }

    const subscription = this.hotelAndMarketService.hotel as Hotel;
    const { extractDatetime } = parity;
    const formattedExtractDate = extractDatetime?.format(
      'YYYY-MM-DD',
    ) as string;

    const hotelinfoId = parity.hotelinfo.id!;
    const membershipType = parity.membershiptype;
    const formattedFromDate = parity.period.day.date.format('YYYY-MM-DD');
    const persons = parity.maxPersons;
    const ota = parity.ota.id!;
    const los = parity.period.los;
    const { platform } = parity;

    console.group(`Debugging info: ${ota} screenshot`);
    const hotelInfoUrl = `${this.ajax.host}/admin/hotel_industry/hotelinfo/${hotelinfoId}/change/`;
    console.log(`%cHotelInfo: ${encodeURI(hotelInfoUrl)}`, CONSOLE_LAYOUT);

    const redirectParams = {
      type: COMPONENT_DOMAINS.PARITY,
      hotel_id: subscription.id!,
      hotelinfo_id: hotelinfoId,
      extract_date: formattedExtractDate,
      from_date: formattedFromDate,
      is_new_integrities: true, // Not required for ratesUrl
      ota,
      los,
    };

    this.logScrapedUrl(redirectParams);
    this.logFullsUrl(redirectParams);
    this.logRatesUrl(redirectParams);

    const multiViewParams = {
      type: COMPONENT_DOMAINS.PARITY,
      hotel_id: subscription.id!,
      'hotelinfo_ids[]': hotelinfoId,
      from_date_range_start: formattedFromDate,
      from_date_range_end: formattedFromDate,
      membershiptype: membershipType,
      rate_type: RATETYPES.NONE,
      meta: 'nested',
      platform,
      persons,
      ota,
      los,
    };

    this.logMultiViewUrl(multiViewParams);

    const liveUrl = formatUrl('/redirect', {
      subscription_id: subscription.id!,
      hotelId: hotelinfoId,
      formattedFromDate,
      direct: false,
      persons,
      ota,
      los,
    });
    const taxConfigParams = {
      subscription_id: subscription.id!,
      currency: parity.currency,
      shop_currency: parity.shopCurrency,
      value: parity.value,
      shop_value: parity.shopValue,
      vat: parity.vat,
      vat_incl: parity.vatIncl,
      city_tax: parity.cityTax,
      city_tax_incl: parity.cityTaxIncl,
      other_taxes: parity.otherTaxes,
      other_taxes_incl: parity.otherTaxesIncl,
      shop_vat_incl: parity.shopVatIncl,
      shop_other_taxes_incl: parity.shopOtherTaxesIncl,
      shop_city_tax_incl: parity.shopCityTaxIncl,
      room_name: parity.roomName,
      cancellation: parity.bestFlex, // Update to bar (OTA-7604)
      extract_datetime: extractDatetime?.format(
        'YYYY-MM-DD HH:mm:ss',
      ) as string,
      mealtype_included: parity.mealtypeIncluded,
      membershiptype: parity.membershiptype,
      max_persons: persons,
      ota,
      hotelinfo: hotelinfoId,
      type: COMPONENT_DOMAINS.PARITY,
      live_url: encodeURIComponent(liveUrl),
    };

    console.table?.(taxConfigParams);
    const taxConfigUrl = formatUrl(
      '/admin/matching/taxconfig/taxconfig_calculation/',
      taxConfigParams,
      this.ajax.host,
    );
    console.log(`%cTax configs: ${encodeURI(taxConfigUrl)}`, CONSOLE_LAYOUT);
    console.groupEnd();
  }

  logParityStrategyIssue(issue: ParityStrategyIssue): void {
    if (!this.access.isStaffUser || !console) {
      return;
    }

    const {
      subscriptionId,
      hotelinfoId,
      formattedExtractDate,
      formattedFromDate,
      membershipType,
      platform,
      persons,
      ota,
      los,
    } = issue.debuggerInfo;

    console.group(`Debugging info: ${ota} - ${formattedExtractDate}`);
    console.table?.(issue.debuggerInfo);

    const hotelInfoUrl = `${this.ajax.host}/admin/hotel_industry/hotelinfo/${hotelinfoId}/change/`;
    console.log(`%cHotelInfo: ${encodeURI(hotelInfoUrl)}`, CONSOLE_LAYOUT);

    const redirectParams = {
      type: COMPONENT_DOMAINS.PARITY,
      hotel_id: subscriptionId,
      hotelinfo_id: hotelinfoId,
      extract_date: formattedExtractDate,
      from_date: formattedFromDate,
      is_new_integrities: true, // Not required for ratesUrl
      ota,
      los,
    };

    this.logScrapedUrl(redirectParams);
    this.logFullsUrl(redirectParams);
    this.logRatesUrl(redirectParams);

    const multiViewParams = {
      type: COMPONENT_DOMAINS.PARITY,
      hotel_id: subscriptionId,
      'hotelinfo_ids[]': hotelinfoId,
      from_date_range_start: formattedFromDate,
      from_date_range_end: formattedFromDate,
      membershiptype: membershipType,
      rate_type: RATETYPES.NONE,
      meta: 'nested',
      platform,
      persons,
      ota,
      los,
    };

    this.logMultiViewUrl(multiViewParams);
    console.groupEnd();
  }

  logRatesUrl(redirectParams: FormatUrlParams): void {
    const path = '/redirect/rates/';
    const { host } = this.ajax;
    const ratesUrl = formatUrl(path, redirectParams, host);
    console.log(`%cRates: ${encodeURI(ratesUrl)}`, CONSOLE_LAYOUT);
  }

  logScrapedUrl(redirectParams: FormatUrlParams): void {
    const path = '/redirect/scraped/';
    const { host } = this.ajax;
    const scrapedUrl = formatUrl(path, redirectParams, host);
    console.log(`%cScraped data: ${encodeURI(scrapedUrl)}`, CONSOLE_LAYOUT);
  }
}

declare module '@ember/service' {
  interface Registry {
    'parity/logger': ParityLoggerService;
  }
}
